import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Layout, Menu, Dropdown, Avatar, Space, Typography, Badge, Tag, Drawer, Grid } from 'antd';
import {
  RadarChartOutlined, DatabaseOutlined, UserOutlined, AppstoreOutlined,
  MenuFoldOutlined, MenuUnfoldOutlined, LogoutOutlined, SettingOutlined,
  KeyOutlined, BellOutlined, ThunderboltOutlined, MobileOutlined,
  ApiOutlined, DesktopOutlined, CloseOutlined, BarChartOutlined, NodeIndexOutlined,
} from '@ant-design/icons';
import { useAuth } from '../context/AuthContext';
import Logo from './Logo';

const { useBreakpoint } = Grid;
const { Header, Sider, Content, Footer } = Layout;
const { Text } = Typography;

const SIDEBAR_BG  = '#0f172a';
const SIDEBAR_SEL = '#2563eb';
const PRIMARY     = '#2563eb';

const buildMenu = (isAdmin) => [
  {
    key: 'conference',
    icon: <RadarChartOutlined />,
    label: 'Conference Tracking',
    children: [
      { key: '/live-tracking',    label: '🔴 Live Tracking' },
      { key: '/events',           label: 'Event Dashboard' },
      { key: '/delegates',        label: 'Delegate Dashboard' },
      { key: '/reader-dashboard', label: 'Reader Dashboard' },
      { key: '/conf-reports',     label: 'Report Master' },
    ],
  },
  {
    key: 'analytics-group',
    icon: <BarChartOutlined />,
    label: 'Analytics',
    children: [
      { key: '/analytics',         label: '📊 Insights' },
      { key: '/smart-insights',    label: '🤖 Smart Insights' },
      { key: '/live-heatmap',      label: '🔥 Heat Map' },
      { key: '/engagement',        label: '🏆 Engagement' },
      { key: '/networking',        label: '🤝 Networking' },
      { key: '/vip-watch',         label: '👑 VIP Watch' },
      { key: '/anomalies',         label: '🚨 Anomalies' },
      { key: '/delegate-journey',  label: '👤 Delegate Journey' },
    ],
  },
  {
    key: 'rfid-ops',
    icon: <ThunderboltOutlined />,
    label: 'RFID Operations',
    children: [
      { key: '/reader-telemetry', label: '📶 Telemetry' },
      { key: '/signal-quality',   label: '📊 Signal (RSSI)' },
      { key: '/live-stream',      label: '⚡ Read Stream' },
      { key: '/coverage-audit',   label: '🔍 Coverage Audit' },
      { key: '/tag-provisioning', label: '🏷 Tag Provisioning' },
    ],
  },
  {
    key: 'conf-masters',
    icon: <AppstoreOutlined />,
    label: 'Masters',
    children: [
      { key: '/categories',     label: 'Category Master' },
      { key: '/conf-locations', label: 'Location Master' },
      { key: '/reader-manager', label: 'Reader Manager' },
    ],
  },
  {
    key: 'api-integrations',
    icon: <ApiOutlined />,
    label: 'API Integrations',
    children: [
      { key: '/reader-api',  label: 'Reader API' },
      { key: '/client-push', label: 'Client API Push' },
    ],
  },
  {
    key: 'welcome-screen',
    icon: <DesktopOutlined />,
    label: 'Welcome Screen',
    children: [
      { key: '/welcome-config',  label: 'Configuration' },
      { key: '/welcome-display', label: 'Display' },
    ],
  },
  { key: '/handheld',      icon: <MobileOutlined />,   label: 'Handheld Simulator' },
  { key: '/raw-inventory', icon: <DatabaseOutlined />, label: 'Raw Inventory' },
  ...(isAdmin ? [{ key: '/db-config', icon: <SettingOutlined />, label: 'DB Config' }] : []),
  ...(isAdmin ? [{ key: '/users',     icon: <UserOutlined />,    label: 'User Management' }] : []),
];

const PAGE_TITLES = {
  '/':                 '🔴 Live Tracking Dashboard',
  '/live-tracking':    '🔴 Live Tracking Dashboard',
  '/events':           'Event Dashboard',
  '/delegates':        'Delegate Dashboard',
  '/categories':       'Category Master',
  '/conf-locations':   'Location Master',
  '/reader-dashboard': 'Reader Dashboard',
  '/reader-manager':   'Reader Manager',
  '/handheld':         'Handheld Simulator',
  '/conf-reports':     'Conference Reports',
  '/raw-inventory':    'Raw Inventory',
  '/db-config':        'Database Configuration',
  '/users':            'User Management',
  '/welcome-config':   'Welcome Screen — Configuration',
  '/welcome-display':  'Welcome Screen — Display',
  '/reader-api':       'Reader API',
  '/client-push':      'Client API Push',
  '/analytics':        'Analytics & Insights',
  '/delegate-journey': 'Delegate Journey',
  '/smart-insights':   '🤖 Smart Insights',
  '/live-heatmap':     '🔥 Live Heat Map',
  '/engagement':       '🏆 Engagement Leaderboard',
  '/networking':       '🤝 Networking Insights',
  '/vip-watch':        '👑 VIP Watch',
  '/anomalies':        '🚨 Anomaly Detection Center',
  '/reader-telemetry': '📶 Reader Telemetry',
  '/signal-quality':   '📊 Signal Quality (RSSI)',
  '/live-stream':      '⚡ Live Read Stream',
  '/coverage-audit':   '🔍 Coverage & Phantom Audit',
  '/tag-provisioning': '🏷 Bulk Tag Provisioning',
};

const ROLE_COLOR = { admin: 'red', operator: 'blue', viewer: 'default' };

export default function AppLayout() {
  const [collapsed, setCollapsed]   = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { user, logoutUser, isAdmin } = useAuth();
  const navigate  = useNavigate();
  const location  = useLocation();
  const screens   = useBreakpoint();
  const isMobile  = !screens.md;
  const isTablet  = screens.md && !screens.lg;

  const handleMenuClick = ({ key }) => {
    const groupKeys = ['conference', 'conf-masters', 'welcome-screen', 'api-integrations'];
    if (!groupKeys.includes(key)) {
      navigate(key);
      if (isMobile) setDrawerOpen(false);
    }
  };

  const userMenu = {
    items: [
      { key: 'profile', icon: <SettingOutlined />, label: 'Profile' },
      { key: 'pwd',     icon: <KeyOutlined />,     label: 'Change Password' },
      { type: 'divider' },
      { key: 'logout',  icon: <LogoutOutlined />,  label: 'Logout', danger: true },
    ],
    onClick: ({ key }) => { if (key === 'logout') logoutUser(); },
  };

  const selectedKey = location.pathname === '/' ? '/live-tracking' : location.pathname;

  const getDefaultOpenKeys = () => {
    const p = location.pathname;
    if (['/live-tracking', '/', '/events', '/delegates', '/reader-dashboard', '/conf-reports'].includes(p)) return ['conference'];
    if (['/categories', '/conf-locations', '/reader-manager'].includes(p)) return ['conf-masters'];
    if (['/welcome-config', '/welcome-display'].includes(p)) return ['welcome-screen'];
    if (['/reader-api', '/client-push'].includes(p)) return ['api-integrations'];
    return ['conference'];
  };

  const sideMenu = (
    <>
      <Logo collapsed={!isMobile && collapsed} />
      <Menu
        theme="dark"
        mode="inline"
        selectedKeys={[selectedKey]}
        defaultOpenKeys={getDefaultOpenKeys()}
        onClick={handleMenuClick}
        style={{ background: SIDEBAR_BG, border: 'none', marginTop: 4 }}
        items={buildMenu(isAdmin)}
      />
      {(!isMobile && !collapsed) && (
        <div style={{ position: 'absolute', bottom: 48, left: 0, right: 0, padding: '8px 16px', borderTop: '1px solid rgba(255,255,255,0.07)' }}>
          <Text style={{ color: 'rgba(255,255,255,0.4)', fontSize: 9, display: 'block', textAlign: 'center' }}>
            Identium Tech Solutions Pvt Ltd
          </Text>
          <Text style={{ color: 'rgba(255,255,255,0.25)', fontSize: 8, display: 'block', textAlign: 'center' }}>
            RFID Delegate Management v2.0 © {new Date().getFullYear()}
          </Text>
        </div>
      )}
      {isMobile && (
        <div style={{ padding: '8px 16px', borderTop: '1px solid rgba(255,255,255,0.07)', marginTop: 8 }}>
          <Text style={{ color: 'rgba(255,255,255,0.4)', fontSize: 9, display: 'block', textAlign: 'center' }}>
            Identium Tech Solutions Pvt Ltd
          </Text>
        </div>
      )}
    </>
  );

  const menuStyles = `
    .ant-menu-dark .ant-menu-item-selected { background-color: ${SIDEBAR_SEL} !important; border-radius: 6px; }
    .ant-menu-dark .ant-menu-item:hover { background-color: rgba(37,99,235,0.25) !important; border-radius: 6px; }
    .ant-menu-dark.ant-menu-inline .ant-menu-item { margin: 2px 8px; width: calc(100% - 16px); }
    .ant-menu-dark .ant-menu-submenu-title:hover { background-color: rgba(37,99,235,0.15) !important; border-radius: 6px; }
    .ant-layout-sider-trigger { background: #1e293b !important; }
    /* Prevent label overflow / overlap in sidebar */
    .ant-menu-inline .ant-menu-item,
    .ant-menu-inline .ant-menu-submenu-title {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .ant-menu-inline .ant-menu-item .ant-menu-title-content,
    .ant-menu-inline .ant-menu-submenu-title .ant-menu-title-content {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      font-size: 13px;
      line-height: 1.4;
    }
    /* Submenu (nested) items get reduced left padding for more text room */
    .ant-menu-inline .ant-menu-sub.ant-menu-inline .ant-menu-item {
      padding-left: 36px !important;
      padding-right: 8px !important;
      font-size: 12.5px;
    }
    /* Tighten icon → label gap so we get more text space */
    .ant-menu-inline .ant-menu-item .ant-menu-item-icon + span,
    .ant-menu-inline .ant-menu-submenu-title .ant-menu-item-icon + span {
      margin-inline-start: 8px;
    }
  `;

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <style>{menuStyles}</style>

      {!isMobile && (
        <Sider
          collapsible collapsed={collapsed} onCollapse={setCollapsed}
          width={260} collapsedWidth={isTablet ? 60 : 80}
          style={{
            background: SIDEBAR_BG, overflow: 'auto',
            height: '100vh', position: 'fixed', left: 0, top: 0, bottom: 0,
            zIndex: 200, boxShadow: '2px 0 16px rgba(0,0,0,0.3)',
          }}
          trigger={null}
        >
          {sideMenu}
        </Sider>
      )}

      {isMobile && (
        <Drawer
          placement="left" open={drawerOpen} onClose={() => setDrawerOpen(false)}
          width={260}
          bodyStyle={{ padding: 0, background: SIDEBAR_BG, height: '100%', overflow: 'auto' }}
          headerStyle={{ display: 'none' }}
          style={{ zIndex: 1000 }}
          closeIcon={null}
        >
          <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '10px 12px 0' }}>
            <span onClick={() => setDrawerOpen(false)}
              style={{ color: 'rgba(255,255,255,0.5)', fontSize: 18, cursor: 'pointer' }}>
              <CloseOutlined />
            </span>
          </div>
          {sideMenu}
        </Drawer>
      )}

      <Layout style={{ marginLeft: isMobile ? 0 : (collapsed ? (isTablet ? 60 : 80) : 260), transition: 'margin-left 0.2s' }}>
        <Header style={{
          padding: isMobile ? '0 12px' : '0 24px', height: 58, lineHeight: '58px',
          position: 'sticky', top: 0, zIndex: 100, background: '#fff',
          boxShadow: '0 1px 0 #e2e8f0, 0 2px 12px rgba(0,0,0,0.06)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <Space align="center" size={isMobile ? 8 : 12} style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
            <span
              onClick={() => isMobile ? setDrawerOpen(true) : setCollapsed(!collapsed)}
              style={{ fontSize: 18, cursor: 'pointer', color: PRIMARY, padding: '0 4px', display: 'flex', alignItems: 'center', flexShrink: 0 }}>
              {(isMobile || collapsed) ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            </span>
            {!isMobile && (
              <Text strong style={{ fontSize: 14, color: '#1e293b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: isTablet ? 180 : 300 }}>
                {PAGE_TITLES[location.pathname] || 'RFID Delegate Management'}
              </Text>
            )}
            {isMobile && (
              <Text strong style={{ fontSize: 13, color: '#1e293b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 140 }}>
                {PAGE_TITLES[location.pathname]?.replace('Dashboard', '').replace('Management', 'Mgmt') || 'RFID'}
              </Text>
            )}
          </Space>

          <Space align="center" size={isMobile ? 8 : 16} style={{ flexShrink: 0 }}>
            {!isMobile && (
              <Tag icon={<ThunderboltOutlined />} style={{
                background: 'linear-gradient(135deg, #10b981, #059669)',
                color: '#fff', border: 'none', fontSize: 11, fontWeight: 600,
                borderRadius: 20, padding: '0 10px',
              }}>LIVE</Tag>
            )}
            <Badge count={0} size="small">
              <BellOutlined style={{ fontSize: 17, color: '#64748b', cursor: 'pointer' }} />
            </Badge>
            <Dropdown menu={userMenu} trigger={['click']}>
              <Space style={{ cursor: 'pointer' }} size={isMobile ? 6 : 8}>
                <Avatar size={isMobile ? 30 : 34} style={{
                  background: `linear-gradient(135deg, ${PRIMARY}, #1d4ed8)`,
                  fontWeight: 700, fontSize: 14, flexShrink: 0,
                }}>
                  {user?.full_name?.[0]?.toUpperCase() || 'U'}
                </Avatar>
                {!isMobile && (
                  <Space direction="vertical" size={0} style={{ lineHeight: 1 }}>
                    <Text style={{ fontSize: 12, fontWeight: 600, display: 'block', color: '#1e293b' }}>{user?.full_name}</Text>
                    <Tag color={ROLE_COLOR[user?.role]} style={{ margin: 0, fontSize: 9, lineHeight: '14px', padding: '0 4px' }}>
                      {user?.role?.toUpperCase()}
                    </Tag>
                  </Space>
                )}
              </Space>
            </Dropdown>
          </Space>
        </Header>

        <Content style={{ padding: isMobile ? 12 : 24, minHeight: 'calc(100vh - 58px - 40px)', background: '#f1f5f9' }}>
          <Outlet />
        </Content>

        {!isMobile && (
          <Footer style={{ textAlign: 'center', padding: '10px 24px', background: '#fff', borderTop: '1px solid #e2e8f0' }}>
            <Text style={{ fontSize: 11, color: '#94a3b8' }}>
              © {new Date().getFullYear()} Identium Tech Solutions Private Limited &nbsp;|&nbsp;
              Plot No. 5, Sant Nagar, East of Kailash, New Delhi - 110065 &nbsp;|&nbsp;
              011-47147839 &nbsp;|&nbsp; RFID Delegate Management System v2.0
            </Text>
          </Footer>
        )}
      </Layout>
    </Layout>
  );
}
